import * as React from "react";
import { useState } from "react";
import { PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { Button } from "@/components/ui";
import { CTA, normalizeCTA } from "@/services/bedrock";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";
import { CTAWizardDialog } from "./CTAWizardDialog";
import { CtaTypeDefinitions, describeCta } from "./ctaTypes";

interface Props {
  value: CTA | null;
  onChange: (cta: CTA | null) => void;
  playerId?: number;
  canEdit?: boolean;
  addLabel?: string;
}

const CallToActionField: React.FC<Props> = ({
  value,
  onChange,
  playerId,
  canEdit = true,
  addLabel = "Add Call to Action",
}) => {
  const [wizardOpen, setWizardOpen] = useState(false);
  const { isPlayer } = useSocialCoachSession();
  const definition = value ? CtaTypeDefinitions[value.type] : null;

  return (
    <>
      {value ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-slate-50 p-3">
          <div className="flex items-center gap-3 min-w-0">
            {definition && <div className="text-primary shrink-0">{definition.icon}</div>}
            <div className="flex flex-col min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate">{value.label || "Untitled Button"}</div>
              <div className="text-xs text-muted-foreground truncate">{describeCta(value, isPlayer)}</div>
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-1 shrink-0">
              <Button type="button" variant="outline" size="sm" onClick={() => setWizardOpen(true)}>
                <PencilIcon className="size-3.5" />
                Edit
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Remove Call to Action"
                onClick={() => onChange(null)}
              >
                <Trash2Icon className="size-3.5 text-muted-foreground" />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start gap-2 border-dashed text-muted-foreground"
          disabled={!canEdit}
          onClick={() => setWizardOpen(true)}
        >
          <PlusIcon className="size-4" />
          {addLabel}
        </Button>
      )}

      <CTAWizardDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        initialCta={value}
        playerId={playerId}
        onConfirm={cta => onChange(normalizeCTA(cta))}
      />
    </>
  );
};

export { CallToActionField };
